import { Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { BrandLoadingBar } from '@/components/ui/brand-loading-bar'
import { cn } from '@/lib/utils'
import { withErrorCode } from '@/lib/errors'
import type { MediaDocument } from '../../services/admin.media.service'

const statusConfig: Record<
  MediaDocument['status'],
  { label: string; variant: 'default' | 'secondary' | 'destructive' }
> = {
  uploading: { label: 'Uploading', variant: 'secondary' },
  processing: { label: 'Processing', variant: 'secondary' },
  ready: { label: 'Ready', variant: 'default' },
  error: {
    label: withErrorCode('Upload could not be completed.', 'media/upload'),
    variant: 'destructive',
  },
}

interface MediaCardProps {
  item: MediaDocument
  selected: boolean
  onClick: () => void
}

export function MediaCard({ item, selected, onClick }: MediaCardProps) {
  const { label, variant } = statusConfig[item.status]
  const isPending = item.status === 'uploading' || item.status === 'processing'

  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-lg border bg-card text-left transition-colors hover:border-primary',
        selected && 'ring-2 ring-primary ring-offset-2',
        isPending && 'overflow-hidden'
      )}
    >
      {/* Thumbnail */}
      <div className="relative aspect-square w-full overflow-hidden bg-muted">
        {item.status === 'ready' && item.url ? (
          <img
            src={item.thumbnailUrl ?? item.url}
            loading="lazy"
            alt={item.name}
            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            {item.status === 'error' ? (
              <span className="text-2xl">✕</span>
            ) : (
              <Loader2 className="h-6 w-6 animate-spin" />
            )}
          </div>
        )}

        {item.status !== 'ready' && (
          <div className="absolute top-1.5 right-1.5">
            <Badge variant={variant} className="text-[10px] px-1.5 py-0.5 leading-tight">
              {label}
            </Badge>
          </div>
        )}
      </div>

      {isPending && <BrandLoadingBar height="sm" className="rounded-none" />}

      {/* Name */}
      <div className="px-2 py-1.5">
        <p className="truncate text-xs font-medium" title={item.name}>
          {item.name}
        </p>
        {item.status === 'ready' && (
          <p className="text-[10px] text-muted-foreground">
            {item.width} × {item.height}
          </p>
        )}
        {item.status === 'processing' && (
          <p className="text-[10px] text-muted-foreground">Processing…</p>
        )}
        {item.status === 'uploading' && (
          <p className="text-[10px] text-muted-foreground">Uploading…</p>
        )}
      </div>
    </button>
  )
}
